import { Skeleton, Typography } from 'antd';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateTicketFormValues, EditTicketFormValues } from '../../../../../../../../types/form';
import CustomField from '../../../../../../../components/CustomField';
import useAgentFormById from '../../../../../../../hooks/ticketDetailPage/useAgentFormById';
import useCurrentTicket from '../../../../../../../hooks/ticketDetailPage/useCurrentTicket';
import useCurrentTicketChannel from '../../../../../../../hooks/ticketDetailPage/useCurrentTicketChannel';
import useCurrentTicketStatus from '../../../../../../../hooks/ticketDetailPage/useCurrentTicketStatus';
import useFieldDefinitionByKey from '../../../../../../../hooks/ticketDetailPage/useFieldDefinitionByKey';
import {
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    TicketSystemFieldsKeyMap,
    channelType
} from '../../../../../../../model/types';
import { getFieldRuleByKey } from '../../../../../../../utils/forms/formHelper.util';
import { getValidationRules } from '../../../../../../../utils/forms/formHelper.validators';
import { getFormattedDateWithDayjs } from '../../../../../../../utils/helpers';
import {
    ConditionController,
    FORM_FIELD_KEY,
    generateReactHookFormFriendlyFieldName
} from '../../../AgentFormActiveFieldsContainer';

const { Text } = Typography;

export default function EditTicketSubjectContainer() {
    const { t } = useTranslation('tickets');
    const { t: tCommon } = useTranslation('common');

    const { data: ticketDetail } = useCurrentTicket();
    const ticketChannel = useCurrentTicketChannel();
    const ticketStatus = useCurrentTicketStatus();

    const formId = useWatch<CreateTicketFormValues, typeof FORM_FIELD_KEY>({ name: FORM_FIELD_KEY });
    const currentAgentForm = useAgentFormById(Number(formId));
    const subjectFieldDefinition = useFieldDefinitionByKey(TicketSystemFieldsKey.subject);

    if (!subjectFieldDefinition) return <Skeleton.Input active className="full-width" />;

    const createdAt = tCommon(getFormattedDateWithDayjs(ticketDetail?.createdAt) as any);
    const isClosedTicket = ticketStatus === 'Closed';

    const fieldRuleOnForm = currentAgentForm?.fields?.find(getFieldRuleByKey(subjectFieldDefinition));

    return (
        <>
            <Controller<
                EditTicketFormValues,
                `${typeof TICKET_FORM_FIELDS_KEY}.${typeof TicketSystemFieldsKeyMap.subject}`
            >
                name={generateReactHookFormFriendlyFieldName(subjectFieldDefinition.key)}
                render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => (
                    <>
                        {fieldRuleOnForm?.conditions && (
                            <ConditionController
                                fieldKey={subjectFieldDefinition.key}
                                conditions={fieldRuleOnForm.conditions}
                            />
                        )}

                        <CustomField
                            containerProps={{
                                errorMessage: error?.message,
                                fieldRequirement: fieldRuleOnForm?.required || subjectFieldDefinition?.required
                            }}
                            disabled={isClosedTicket}
                            fieldDefinition={subjectFieldDefinition}
                            value={value}
                            onBlur={onBlur}
                            onChange={onChange}
                            antdProps={{ ref }}
                        />
                    </>
                )}
                rules={{
                    validate: getValidationRules({
                        fieldRuleOnForm,
                        fieldDefinition: subjectFieldDefinition
                    })
                }}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
                {t('subjectHelperText', {
                    createdAt,
                    ticketChannel: t(`channelNames.${ticketChannel as channelType}`)
                })}
            </Text>
        </>
    );
}
